import { responseStatus } from "utils/types";
import { request } from "utils/request";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "utils/redux/store";
import { REACT_APP_API_URL } from "utils/environmentVariables";
import {
  UserVisibilitySettings,
  UserWeeklyCheckInSettings,
  UserSubordinates,
  UserAbsence,
  UserAbsences,
  PlatformNotificationSettings,
  UpdatePlatformNotificationSettingsPayload,
} from "./types";
import { toast } from "react-toastify";
import { successNotificationOptions } from "utils/constants";
import { updateUserAbsences } from "./helpers";
import { updateUsersAction } from "../Global/slice";

export interface UserSettingsState {
  userVisibilitySetting: {
    [userAccountId: number]: UserVisibilitySettings;
  };
  userSubordinates: { [userAccountId: number]: UserSubordinates };
  userNotificationsAndEmailSettings: UserWeeklyCheckInSettings | null;
  getUserSubordinatesStatus: responseStatus;
  getUserNotificationsAndEmailSettingsStatus: responseStatus;
  updateUserNotificationsAndEmailSettingsStatus: responseStatus;
  getUserSettingsStatus: responseStatus;
  editUserSettingsStatus: responseStatus;
  getUserSubordinatesByUserAccountIdStatus: {
    [userAccountId: number]: responseStatus;
  };
  userAbsences: { [userAccountId: number]: UserAbsences };
  getAllUsersAbsencesStatus: responseStatus;
  getUserAbsencesStatus: responseStatus;
  createUserAbsenceStatus: responseStatus;
  updateUserAbsenceStatus: responseStatus;
  resetPasswordStatus: responseStatus;
  resetPasswordError: string | null;
  updateUserTimezoneStatus: responseStatus;
  platformNotificationSettings: PlatformNotificationSettings | null;
  getPlatformNotificationSettingsStatus: responseStatus;
  updatePlatformNotificationSettingsStatus: responseStatus;
}

export const initialState: UserSettingsState = {
  userVisibilitySetting: {},
  userSubordinates: {},
  userNotificationsAndEmailSettings: null,
  userAbsences: {},
  getAllUsersAbsencesStatus: "idle",
  getUserAbsencesStatus: "idle",
  createUserAbsenceStatus: "idle",
  updateUserAbsenceStatus: "idle",
  getUserSubordinatesStatus: "idle",
  getUserNotificationsAndEmailSettingsStatus: "idle",
  updateUserNotificationsAndEmailSettingsStatus: "idle",
  getUserSettingsStatus: "idle",
  editUserSettingsStatus: "idle",
  getUserSubordinatesByUserAccountIdStatus: {},
  resetPasswordStatus: "idle",
  resetPasswordError: null,
  updateUserTimezoneStatus: "idle",
  platformNotificationSettings: null,
  getPlatformNotificationSettingsStatus: "idle",
  updatePlatformNotificationSettingsStatus: "idle",
};

export const getUserVisibilitySettings = createAsyncThunk(
  "userSettings/getUserVisibilitySettings",
  async ({
    userAccountId,
  }: {
    userAccountId: number;
    forceRefresh?: boolean;
  }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/users/${userAccountId}/visibility`;
    const response = (await request(requestUrl)) as UserVisibilitySettings;
    return {
      userAccountId,
      visibilitySettings: response,
    };
  },
  {
    condition: ({ userAccountId, forceRefresh }, { getState }) => {
      const userVisibilitySetting = (getState() as RootState).userSettings
        .userVisibilitySetting[userAccountId];
      if (userVisibilitySetting && !forceRefresh) {
        return false;
      }
    },
  }
);

export const getUserNotificationsAndEmailSettings = createAsyncThunk(
  "userSettings/getUserNotificationsAndEmailSettings",
  async (_: { forceRefresh?: boolean } | undefined, { getState }) => {
    const { currentUserAccountId } = (getState() as RootState).global;
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/users/${currentUserAccountId}/weeklyCheckInsUserSettings`;
    const response = (await request(requestUrl)) as UserWeeklyCheckInSettings;
    return {
      notificationsAndEmailSettings: response,
    };
  },
  {
    condition: (payload, { getState }) => {
      const { forceRefresh } = payload ?? {};
      const { userSettings } = getState() as RootState;
      const userNotificationsAndEmailSettings =
        userSettings.userNotificationsAndEmailSettings;
      if (userNotificationsAndEmailSettings && !forceRefresh) {
        return false;
      }
    },
  }
);

export const getUserSubordinates = createAsyncThunk(
  "userSettings/getUserSubordinates",
  async (userAccountIds: number[], { getState, dispatch }) => {
    const { getUserSubordinatesByUserAccountIdStatus } = (
      getState() as RootState
    ).userSettings;

    const userAccountIdsToSend: number[] = [];

    userAccountIds.forEach((userAccountId) => {
      const userStatus =
        getUserSubordinatesByUserAccountIdStatus[userAccountId];
      if (userStatus !== "loading" && userStatus !== "succeeded") {
        userAccountIdsToSend.push(userAccountId);
      }
    });

    if (userAccountIdsToSend.length === 0) {
      throw new Error("All subordinates must be loaded or pending");
    }

    dispatch(
      setUserSubordinatesStatuses({
        userAccountIds: userAccountIdsToSend,
        status: "loading",
      })
    );

    const requestUrl = `${REACT_APP_API_URL}/talentInsights/userSubordinates?userAccountIds=${userAccountIds.join(
      ","
    )}`;
    const response = (await request(requestUrl)) as {
      [userAccountId: number]: UserSubordinates;
    };

    dispatch(
      setUserSubordinatesStatuses({
        userAccountIds: userAccountIdsToSend,
        status: "succeeded",
      })
    );
    return response;
  }
);

export const updateUserVisibilitySettings = createAsyncThunk(
  "userSettings/updateUserVisibilitySettings",
  async (
    {
      payload,
      userAccountId,
    }: {
      payload: Partial<UserVisibilitySettings>;
      userAccountId: number;
    },
    { getState }
  ) => {
    const existingVisibilitySettings = (getState() as RootState).userSettings
      .userVisibilitySetting[userAccountId];
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/users/${userAccountId}/visibility`;
    await request(requestUrl, {
      method: "PUT",
      body: JSON.stringify({ ...existingVisibilitySettings, ...payload }),
    });

    return {
      userAccountId,
      visibilitySettings: {
        ...payload,
      },
    };
  }
);

export const updateUserNotificationsAndEmailSettings = createAsyncThunk(
  "userSettings/updateUserNotificationsAndEmailSettings",
  async (
    payload: Partial<
      Pick<
        UserWeeklyCheckInSettings,
        | "hasCoachBoReminderEmailsEnabled"
        | "hasWeeklyTeamLeaderReportEmailsEnabled"
        | "hasWeeklySeniorManagerReportEmailsEnabled"
        | "hasWeeklyExecutiveReportEmailsEnabled"
        | "hasWeeklyCheckInsEnabledForDirectReports"
        | "hasWeeklyCheckInsEnabledForReportingStructure"
        | "hasCoachBoEmailsEnabled"
      >
    >,
    { getState }
  ) => {
    const { currentUserAccountId } = (getState() as RootState).global;
    const existingNotificationsAndEmailSettings = (getState() as RootState)
      .userSettings.userNotificationsAndEmailSettings;
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/users/${currentUserAccountId}/weeklyCheckInsUserSettings`;
    await request(requestUrl, {
      method: "PUT",
      body: JSON.stringify({
        ...existingNotificationsAndEmailSettings,
        ...payload,
      }),
    });

    return {
      notificationsAndEmailSettings: {
        ...payload,
      },
    };
  }
);

export const getAllUsersAbsences = createAsyncThunk(
  "userSettings/getAllUsersAbsences",
  async (_: { forceRefresh?: boolean } | undefined) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/usersAbsences`;
    const response = (await request(requestUrl)) as {
      [userAccountId: number]: UserAbsences;
    };
    return response;
  },
  {
    condition: (payload, { getState }) => {
      const { forceRefresh } = payload ?? {};
      const { getAllUsersAbsencesStatus } = (getState() as RootState)
        .userSettings;
      return forceRefresh || getAllUsersAbsencesStatus === "idle";
    },
  }
);

export const getUserAbsences = createAsyncThunk(
  "userSettings/getUserAbsences",
  async ({ userAccountId }: { userAccountId: number }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/userAbsences/${userAccountId}`;
    const response = (await request(requestUrl)) as UserAbsences;
    return { response, userAccountId };
  }
);

export const createUserAbsence = createAsyncThunk(
  "userSettings/createUserAbsence",
  async ({
    userAccountId,
    leaveStartDate,
    leaveEndDate,
    leaveReason,
  }: Omit<UserAbsence, "id" | "wasCancelled"> & {
    userAccountId: number;
  }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/userAbsences`;
    const response = (await request(requestUrl, {
      method: "POST",
      body: JSON.stringify({
        userAccountId,
        leaveStartDate,
        leaveEndDate,
        leaveReason,
      }),
    })) as UserAbsence;
    toast.success("Absence created successfully", {
      ...successNotificationOptions,
    });

    const userAbsences = updateUserAbsences(
      {
        activeAbsence: null,
        pastAbsences: [],
        futureAbsence: null,
      },
      response
    );

    return {
      userAbsences,
      userAccountId,
    };
  }
);

export const updateUserAbsence = createAsyncThunk(
  "userSettings/updateUserAbsence",
  async (
    {
      absenceId,
      leaveStartDate,
      leaveEndDate,
      leaveReason,
      wasCancelled,
      userAccountId,
    }: Partial<UserAbsence> & { absenceId: number; userAccountId: number },
    { dispatch, getState }
  ) => {
    const {
      userSettings: { userAbsences },
    } = getState() as RootState;
    const currentAbsence = userAbsences[userAccountId];
    if (!currentAbsence) {
      throw new Error("User absences not found");
    }

    const requestUrl = `${REACT_APP_API_URL}/talentInsights/userAbsences/${absenceId}`;
    const response = (await request(requestUrl, {
      method: "PUT",
      body: JSON.stringify({
        leaveStartDate,
        leaveEndDate,
        leaveReason,
        wasCancelled,
      }),
    })) as UserAbsence;

    if (wasCancelled) {
      const leaveStartDate = new Date(response.leaveStartDate);
      const type = leaveStartDate < new Date() ? "active" : "future";
      toast.success("Absence cancelled successfully", {
        ...successNotificationOptions,
      });
      dispatch(removeUserAbsence({ userAccountId, type }));
      return null;
    }

    toast.success("Absence updated successfully", {
      ...successNotificationOptions,
    });

    return {
      updatedAbsences: updateUserAbsences(currentAbsence, response),
      userAccountId,
    };
  }
);

export const resetPassword = createAsyncThunk(
  "userSettings/resetPassword",
  async (
    {
      currentPassword,
      newPassword,
    }: {
      currentPassword: string;
      newPassword: string;
    },
    { dispatch }
  ) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/passwordResetInPlatform`;
    const response = (await request(
      requestUrl,
      {
        method: "PUT",
        body: JSON.stringify({
          currentPassword,
          newPassword,
          confirmNewPassword: newPassword,
        }),
      },
      true
    )) as {
      error?: string;
      message?: string;
      status: number;
    };

    if (response.error) {
      dispatch(setResetPasswordError(response.error));
      throw new Error(response.error);
    }

    return { success: true };
  }
);

export const updateUserTimezone = createAsyncThunk(
  "userSettings/updateUserTimezone",
  async (timezone: string, { dispatch, getState }) => {
    const { currentUserAccountId } = (getState() as RootState).global;
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/userTimezone`;
    const response = await request(requestUrl, {
      method: "PUT",
      body: JSON.stringify({ timezone }),
    });

    if (currentUserAccountId) {
      dispatch(updateUsersAction({ [currentUserAccountId]: { timezone } }));
    }

    toast.success("Timezone updated successfully", successNotificationOptions);

    return response;
  }
);

export const getPlatformNotificationSettings = createAsyncThunk(
  "userSettings/getPlatformNotificationSettings",
  async (_: { forceRefresh?: boolean } | undefined) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/notification-settings`;

    const response = (await request(requestUrl)) as {
      status: boolean;
      settings: PlatformNotificationSettings;
    };

    return { platformNotificationSettings: response.settings };
  },
  {
    condition: (payload, { getState }) => {
      const { forceRefresh } = payload ?? {};
      const { userSettings } = getState() as RootState;
      if (userSettings.platformNotificationSettings && !forceRefresh) {
        return false;
      }
    },
  }
);

export const updatePlatformNotificationSettings = createAsyncThunk(
  "userSettings/updatePlatformNotificationSettings",
  async (payload: UpdatePlatformNotificationSettingsPayload) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/notification-settings`;

    let body: Record<string, number>;

    if ("platform" in payload) {
      body = {
        [payload.platform]: payload.value,
      };
    } else {
      body = {
        notification_minutes_before: payload.notification_minutes_before,
      };
    }

    await request(requestUrl, {
      method: "PUT",
      body: JSON.stringify(body),
    });

    return payload;
  }
);

export const userSettingsSlice = createSlice({
  name: "userSettings",
  initialState,
  reducers: {
    resetUserVisibilitySettings: (state) => {
      state.userVisibilitySetting = {};
      state.getUserSettingsStatus = "idle";
      state.editUserSettingsStatus = "idle";
    },
    resetUserSubordinates: (state) => {
      state.userSubordinates = {};
    },
    setUserSubordinatesStatuses: (
      state,
      {
        payload: { userAccountIds, status },
      }: PayloadAction<{
        status: responseStatus;
        userAccountIds: number[];
      }>
    ) => {
      const getUserSubordinatesByUserAccountIdStatus = {
        ...state.getUserSubordinatesByUserAccountIdStatus,
      };
      userAccountIds.forEach((userAccountId) => {
        getUserSubordinatesByUserAccountIdStatus[userAccountId] = status;
      });
      return {
        ...state,
        getUserSubordinatesByUserAccountIdStatus,
      };
    },
    setResetPasswordError: (state, action: PayloadAction<string | null>) => {
      state.resetPasswordError = action.payload;
    },
    resetResetPasswordStatus: (state) => {
      state.resetPasswordStatus = "idle";
    },
    removeUserAbsence: (
      state,
      {
        payload: { userAccountId, type = "active" },
      }: PayloadAction<{ userAccountId: number; type?: "future" | "active" }>
    ) => {
      return {
        ...state,
        userAbsences: {
          ...state.userAbsences,
          [userAccountId]: {
            ...state.userAbsences[userAccountId],
            [type === "active" ? "activeAbsence" : "futureAbsence"]: null,
          },
        },
      };
    },
    addUserAbsence: (
      state,
      {
        payload: { userAccountId, type = "active", absence },
      }: PayloadAction<{
        userAccountId: number;
        type?: "future" | "active";
        absence: UserAbsence;
      }>
    ) => {
      return {
        ...state,
        userAbsences: {
          ...state.userAbsences,
          [userAccountId]: {
            ...state.userAbsences[userAccountId],
            [type === "active" ? "activeAbsence" : "futureAbsence"]: absence,
          },
        },
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserVisibilitySettings.pending, (state) => {
        state.getUserSettingsStatus = "loading";
      })
      .addCase(getUserVisibilitySettings.fulfilled, (state, action) => {
        return {
          ...state,
          getUserSettingsStatus: "succeeded",
          userVisibilitySetting: {
            ...state.userVisibilitySetting,
            [action.payload.userAccountId]: action.payload.visibilitySettings,
          },
        };
      })
      .addCase(getUserVisibilitySettings.rejected, (state) => {
        state.getUserSettingsStatus = "failed";
      })
      .addCase(updateUserVisibilitySettings.pending, (state) => {
        state.editUserSettingsStatus = "loading";
      })
      .addCase(updateUserVisibilitySettings.fulfilled, (state, action) => {
        toast.success(
          "User settings updated successfully",
          successNotificationOptions
        );
        return {
          ...state,
          editUserSettingsStatus: "succeeded",
          userVisibilitySetting: {
            ...state.userVisibilitySetting,
            [action.payload.userAccountId]: {
              ...state.userVisibilitySetting[action.payload.userAccountId],
              ...action.payload.visibilitySettings,
            },
          },
        };
      })
      .addCase(updateUserVisibilitySettings.rejected, (state) => {
        state.editUserSettingsStatus = "failed";
      })
      .addCase(getUserNotificationsAndEmailSettings.pending, (state) => {
        state.getUserNotificationsAndEmailSettingsStatus = "loading";
      })
      .addCase(
        getUserNotificationsAndEmailSettings.fulfilled,
        (state, action) => {
          return {
            ...state,
            getUserNotificationsAndEmailSettingsStatus: "succeeded",
            userNotificationsAndEmailSettings:
              action.payload.notificationsAndEmailSettings,
          };
        }
      )
      .addCase(getUserNotificationsAndEmailSettings.rejected, (state) => {
        state.getUserNotificationsAndEmailSettingsStatus = "failed";
      })
      .addCase(updateUserNotificationsAndEmailSettings.pending, (state) => {
        state.updateUserNotificationsAndEmailSettingsStatus = "loading";
      })
      .addCase(
        updateUserNotificationsAndEmailSettings.fulfilled,
        (state, action) => {
          if (state.userNotificationsAndEmailSettings === null) {
            return {
              ...state,
              updateUserNotificationsAndEmailSettingsStatus: "succeeded",
            };
          }

          toast.success(
            "User notifications/emails updated successfully",
            successNotificationOptions
          );

          return {
            ...state,
            updateUserNotificationsAndEmailSettingsStatus: "succeeded",
            userNotificationsAndEmailSettings: {
              ...state.userNotificationsAndEmailSettings,
              ...action.payload.notificationsAndEmailSettings,
            },
          };
        }
      )
      .addCase(updateUserNotificationsAndEmailSettings.rejected, (state) => {
        state.updateUserNotificationsAndEmailSettingsStatus = "failed";
      })
      .addCase(getUserSubordinates.pending, (state) => {
        state.getUserSubordinatesStatus = "loading";
      })
      .addCase(getUserSubordinates.fulfilled, (state, action) => {
        return {
          ...state,
          getUserSubordinatesStatus: "succeeded",
          userSubordinates: {
            ...state.userSubordinates,
            ...action.payload,
          },
        };
      })
      .addCase(getUserSubordinates.rejected, (state) => {
        state.getUserSubordinatesStatus = "failed";
      })
      .addCase(resetPassword.pending, (state) => {
        state.resetPasswordStatus = "loading";
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.resetPasswordStatus = "succeeded";
        state.resetPasswordError = null;
        toast.success(
          "Password reset successfully",
          successNotificationOptions
        );
      })
      .addCase(resetPassword.rejected, (state) => {
        state.resetPasswordStatus = "failed";
      })
      .addCase(getAllUsersAbsences.pending, (state) => {
        state.getAllUsersAbsencesStatus = "loading";
      })
      .addCase(getAllUsersAbsences.fulfilled, (state, action) => {
        return {
          ...state,
          getAllUsersAbsencesStatus: "succeeded",
          userAbsences: {
            ...state.userAbsences,
            ...action.payload,
          },
        };
      })
      .addCase(getAllUsersAbsences.rejected, (state) => {
        state.getAllUsersAbsencesStatus = "failed";
      })
      .addCase(getUserAbsences.pending, (state) => {
        state.getUserAbsencesStatus = "loading";
      })
      .addCase(
        getUserAbsences.fulfilled,
        (
          state,
          {
            payload,
          }: PayloadAction<{ response: UserAbsences; userAccountId: number }>
        ) => {
          return {
            ...state,
            getUserAbsencesStatus: "succeeded",
            userAbsences: {
              ...state.userAbsences,
              [payload.userAccountId]: payload.response,
            },
          };
        }
      )
      .addCase(getUserAbsences.rejected, (state) => {
        state.getUserAbsencesStatus = "failed";
      })
      .addCase(createUserAbsence.pending, (state) => {
        state.createUserAbsenceStatus = "loading";
      })
      .addCase(
        createUserAbsence.fulfilled,
        (
          state,
          action: PayloadAction<{
            userAbsences: UserAbsences;
            userAccountId: number;
          }>
        ) => {
          return {
            ...state,
            createUserAbsenceStatus: "succeeded",
            userAbsences: {
              ...state.userAbsences,
              [action.payload.userAccountId]: action.payload.userAbsences,
            },
          };
        }
      )
      .addCase(createUserAbsence.rejected, (state) => {
        state.createUserAbsenceStatus = "failed";
      })
      .addCase(updateUserAbsence.pending, (state) => {
        state.updateUserAbsenceStatus = "loading";
      })
      .addCase(
        updateUserAbsence.fulfilled,
        (
          state,
          action: PayloadAction<{
            updatedAbsences: UserAbsences;
            userAccountId: number;
          } | null>
        ) => {
          if (!action.payload) {
            return {
              ...state,
              updateUserAbsenceStatus: "succeeded",
            };
          }

          return {
            ...state,
            updateUserAbsenceStatus: "succeeded",
            userAbsences: {
              ...state.userAbsences,
              [action.payload.userAccountId]: action.payload.updatedAbsences,
            },
          };
        }
      )
      .addCase(updateUserAbsence.rejected, (state) => {
        state.updateUserAbsenceStatus = "failed";
      })
      .addCase(updateUserTimezone.pending, (state) => {
        state.updateUserTimezoneStatus = "loading";
      })
      .addCase(updateUserTimezone.fulfilled, (state) => {
        state.updateUserTimezoneStatus = "succeeded";
      })
      .addCase(updateUserTimezone.rejected, (state) => {
        state.updateUserTimezoneStatus = "failed";
      })
      .addCase(getPlatformNotificationSettings.pending, (state) => {
        state.getPlatformNotificationSettingsStatus = "loading";
      })
      .addCase(getPlatformNotificationSettings.fulfilled, (state, action) => {
        state.getPlatformNotificationSettingsStatus = "succeeded";
        state.platformNotificationSettings =
          action.payload.platformNotificationSettings;
      })
      .addCase(getPlatformNotificationSettings.rejected, (state) => {
        state.getPlatformNotificationSettingsStatus = "failed";
      })
      .addCase(updatePlatformNotificationSettings.pending, (state) => {
        state.updatePlatformNotificationSettingsStatus = "loading";
      })
      .addCase(updatePlatformNotificationSettings.fulfilled, (state, action) => {
        if (!state.platformNotificationSettings) {
          state.updatePlatformNotificationSettingsStatus = "succeeded";
          return;
        }

        // Case 1: Platform toggle update
        if ("platform" in action.payload) {
          const { platform, value } = action.payload;

          state.platformNotificationSettings.final[platform] = !!value;

          toast.success(
            "Notification setting updated successfully",
            successNotificationOptions
          );

          state.updatePlatformNotificationSettingsStatus = "succeeded";
          return;
        }

        // Case 2: Reminder minutes update
        const { notification_minutes_before } = action.payload;

        state.platformNotificationSettings.notification_minutes_before =
          notification_minutes_before;

        toast.success(
          "Notification setting updated successfully",
          successNotificationOptions
        );

        state.updatePlatformNotificationSettingsStatus = "succeeded";
      })
      .addCase(updatePlatformNotificationSettings.rejected, (state) => {
        state.updatePlatformNotificationSettingsStatus = "failed";
      });
  },
});

export const selectGetUserSettingsStatus = (state: RootState) =>
  state.userSettings.getUserSettingsStatus;
export const selectLoggedInUsersVisibilitySettings = (state: RootState) => {
  const { currentUserAccountId } = state.global;
  return currentUserAccountId
    ? state.userSettings.userVisibilitySetting[currentUserAccountId]
    : null;
};
export const selectUserVisibilitySettingsByUserAccountId =
  (userAccountId: number) => (state: RootState) =>
    state.userSettings.userVisibilitySetting[userAccountId];
export const selectGetUserNotificationsAndEmailSettingsStatus = (
  state: RootState
) => state.userSettings.getUserNotificationsAndEmailSettingsStatus;
export const selectUpdateUserNotificationsAndEmailSettingsStatus = (
  state: RootState
) => state.userSettings.updateUserNotificationsAndEmailSettingsStatus;
export const selectUserNotificationsAndEmailSettings = (state: RootState) =>
  state.userSettings.userNotificationsAndEmailSettings;
export const selectGetUserSubordinatesStatus = (state: RootState) =>
  state.userSettings.getUserSubordinatesStatus;
export const selectUserSubordinatesByUserAccountId =
  (userAccountId: number) => (state: RootState) =>
    state.userSettings.userSubordinates[userAccountId];
export const selectAllUserSubordinates = (state: RootState) =>
  state.userSettings.userSubordinates;
export const selectResetPasswordStatus = (state: RootState) =>
  state.userSettings.resetPasswordStatus;
export const selectResetPasswordError = (state: RootState) =>
  state.userSettings.resetPasswordError;
export const selectGetAllUsersAbsencesStatus = (state: RootState) =>
  state.userSettings.getAllUsersAbsencesStatus;
export const selectGetUserAbsencesStatus = (state: RootState) =>
  state.userSettings.getUserAbsencesStatus;
export const selectUserAbsencesByUserAccountId =
  (userAccountId: number) => (state: RootState) =>
    state.userSettings.userAbsences[userAccountId];
export const selectCreateUserAbsenceStatus = (state: RootState) =>
  state.userSettings.createUserAbsenceStatus;
export const selectUpdateUserAbsenceStatus = (state: RootState) =>
  state.userSettings.updateUserAbsenceStatus;
export const selectGetAllUsersAbsences = (state: RootState) =>
  state.userSettings.userAbsences;
export const selectUpdateUserTimezoneStatus = (state: RootState) =>
  state.userSettings.updateUserTimezoneStatus;

export const selectPlatformNotificationSettings = (state: RootState) =>
  state.userSettings.platformNotificationSettings;

export const selectGetPlatformNotificationSettingsStatus = (
  state: RootState
) => state.userSettings.getPlatformNotificationSettingsStatus;

export const selectUpdatePlatformNotificationSettingsStatus = (
  state: RootState
) => state.userSettings.updatePlatformNotificationSettingsStatus;

export const {
  resetUserVisibilitySettings,
  resetUserSubordinates,
  setUserSubordinatesStatuses,
  setResetPasswordError,
  resetResetPasswordStatus,
  removeUserAbsence,
  addUserAbsence,
} = userSettingsSlice.actions;

export default userSettingsSlice.reducer;
